import React, { useEffect, useState } from 'react';
import { Button, Typography } from 'antd';
import firebase from 'firebase/app';

import { useResendCooldown } from '../../models/useResendCooldown'; // adjust path
import { getAppBaseUrl } from '../../utils/URLBuilder';

const { Paragraph } = Typography;

export const ResendVerificationController = ({ onVerified, url }) => {
  const [cooldownRemaining, triggerCooldown] = useResendCooldown();
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [errorText, setErrorText] = useState('');
  const [pollingActive, setPollingActive] = useState(true);

  // ⏱ Poll every 5 seconds to check email verification
  useEffect(() => {
    if (!pollingActive) return;

    const interval = setInterval(async () => {
      const user = firebase.auth().currentUser;
      if (!user) return;

      try {
        let finalVerified = false;

        // Check Firestore first (no auth side-effects)
        try {
          const userDoc = await firebase.firestore().collection('users').doc(user.uid).get();
          if (userDoc.exists && userDoc.data().emailVerified === true) {
            finalVerified = true;
          }
        } catch (fsErr) {
          console.warn('Polling Firestore check failed:', fsErr?.message);
        }

        // Fall back to Auth reload if Firestore hasn't caught up
        if (!finalVerified) {
          try {
            await user.reload();
            finalVerified = !!user.emailVerified;
          } catch (reloadErr) {
            console.warn('Polling reload() failed (non-fatal):', reloadErr?.message);
          }
        }

        if (finalVerified) {
          clearInterval(interval);
          setPollingActive(false);
          setMessageText('✅ Email verified!');
          if (onVerified) onVerified();
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [pollingActive, onVerified]);

  const handleResend = async () => {
    const user = firebase.auth().currentUser;
    if (!user) {
      setMessageText('');
      setErrorText('No user is currently signed in.');
      return;
    }

    setSending(true);
    setMessageText('');
    setErrorText('');

    try {
      const pageParams = new URLSearchParams(window.location.search);
      const redirect = pageParams.get('redirect');
      const redirectRoute = pageParams.get('redirectRoute');
      const params = new URLSearchParams();
      if (redirect) params.set('redirect', redirect);
      else if (redirectRoute) params.set('redirectRoute', redirectRoute);
      const qs = params.toString();
      const continueUrl = `${getAppBaseUrl()}/client/login${qs ? `?${qs}` : ''}`;
      const sendVerificationEmail = firebase.functions().httpsCallable('userService-humanHandler-sendVerificationEmail');
      const result = await sendVerificationEmail({ continueUrl });

      if (result.data && result.data.alreadyVerified) {
        setMessageText('Email is already verified!');
        if (onVerified) onVerified();
      } else {
        if (result.data && result.data.verificationLink) {
          console.log('%c[EMULATOR] Email verification link:', 'color: #ff0098; font-weight: bold;', result.data.verificationLink);
        }
        triggerCooldown();
        setMessageText('Verification email sent. Please check your inbox.');
      }
    } catch (err) {
      console.error('[ResendVerificationController] SendGrid send failed:', err);
      setErrorText('Failed to resend verification email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleLogout = async () => {
    try {
      await firebase.auth().signOut();
      setMessageText('You have been successfully logged out...');
      if (onVerified) onVerified();
    } catch (err) {
      setErrorText('Something went wrong logging out. Please try again.');
    }
  };  

  const currentEmail = firebase.auth().currentUser?.email;

  return (
    <div style={{ 
      marginTop: 24,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px'
    }}>
      {/* Resend Button - Always visible */}
      <Button
        type="primary"
        disabled={cooldownRemaining > 0 || sending}
        loading={sending}
        onClick={() => handleResend()}
      >
        {cooldownRemaining > 0
          ? `Resend in ${cooldownRemaining}s`
          : 'Resend Verification Email'}
      </Button>

      {/* Conditional content */}
      {url && window.location.hostname === 'localhost' && (
        <Paragraph>
          Post verification email url <strong>{url}</strong>
        </Paragraph>
      )}

      {currentEmail && (
        <Paragraph>
          Verification email sent to <strong>{currentEmail}</strong>
        </Paragraph>
      )}

      {errorText && (
        <Paragraph style={{ color: 'red' }}>
          {errorText}
        </Paragraph>
      )}

      {messageText && (
        <Paragraph style={{ color: 'green' }}>
          {messageText}
        </Paragraph>
      )}

      {/* Logout Button - Always visible */}
      <Button
        type="default"
        danger
        onClick={handleLogout}
      >
        Logout
      </Button>
    </div>
  );
};
